import { ChevronDown, ChevronUp } from "lucide-react";
import { Card } from "./card";

interface ExpandableCardProps {
  title?: string;
  canExpand: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  expandedContent?: React.ReactNode;
  /**
   * The two callers disagree by one shade — MeasurementCard uses gray-500 and
   * CompositionCard gray-400. Parameterised so the extraction changes nothing
   * visually; unifying them is a separate, deliberate decision.
   */
  chevronClassName?: string;
  /**
   * MeasurementCard's header carries `cursor-pointer`; CompositionCard's does
   * not. Parameterised for the same reason as chevronClassName — the
   * extraction must not silently add or remove an affordance either card
   * currently has.
   */
  headerClassName?: string;
  testId?: string;
}

export const ExpandableCard = ({
  title,
  canExpand,
  isExpanded,
  onToggle,
  children,
  expandedContent,
  chevronClassName = "text-gray-400",
  headerClassName = "",
  testId,
}: ExpandableCardProps) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canExpand) return;
    onToggle();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!canExpand) return;
    if (e.key === "Enter") {
      onToggle();
    } else if (e.key === " ") {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <Card
      data-testid={testId}
      className={`p-6 backdrop-blur-lg bg-card/80 border border-gray-200/20 animate-fade-in hover:shadow-lg transition-all duration-300 ${
        isExpanded ? "md:col-span-3 lg:col-span-2" : ""
      }`}
    >
      <div className="flex flex-col space-y-4">
        <div
          className={`flex items-center justify-between ${headerClassName}`}
          onClick={handleClick}
          {...(canExpand
            ? {
                role: "button",
                tabIndex: 0,
                "aria-expanded": isExpanded,
                onKeyDown: handleKeyDown,
              }
            : {})}
        >
          {title && (
            <h3 className="font-mono text-[9.5px] font-medium uppercase leading-none tracking-[0.14em] text-muted-foreground">
              {title}
            </h3>
          )}
          {canExpand &&
            (isExpanded ? (
              <ChevronUp className={`w-4 h-4 ${chevronClassName}`} />
            ) : (
              <ChevronDown className={`w-4 h-4 ${chevronClassName}`} />
            ))}
        </div>

        {children}

        {isExpanded && expandedContent && (
          <div className="mt-6 h-[300px] animate-fade-in">{expandedContent}</div>
        )}
      </div>
    </Card>
  );
};
